import { lazy } from 'react'
import type { PagesType } from '@/common/services/models/pages'
import { HomePage } from './home/HomePage'

export const Pages: PagesType = {
    Home: {
        component: HomePage,
        urls: ['/'],
    },
    Generic: {
        component: lazy(() => import('./generic/GenericPage')),
        urls: ['/'],
    },
    Cinemas: {
        component: lazy(() => import('./cinemas/allCinemas/AllCinemasPage')),
        urls: ['/'],
    },
    Cinema: {
        component: lazy(() => import('@/pages/cinemas/cinema/CinemaPage')),
        urls: ['/'],
    },
    GiftShop: {
        component: lazy(() => import('../pages/giftShop/giftShopLanding/GiftShopPage')),
        urls: ['/'],
    },
    GiftShopCustomerDetails: {
        component: lazy(() => import('../pages/giftShop/giftShopCustomerDetails/GiftShopCustomerDetailPage')),
        urls: ['/'],
    },
    GiftShopPayment: {
        component: lazy(() => import('../pages/giftShop/giftShopPayment/GiftShopPaymentPage')),
        urls: ['/'],
    },
    GiftCardConfirmation: {
        component: lazy(() => import('../pages/giftShop/giftCardConfirmation/GiftCardConfirmationPage')),
        urls: ['/'],
    },
    TicketBundlesCustomerDetails: {
        component: lazy(
            () => import('../pages/ticketBundles/ticketBundlesCustomerDetails/TicketBundlesCustomerDetailsPage')
        ),
        urls: ['/'],
    },
    TicketBundleConfirmation: {
        component: lazy(() => import('../pages/ticketBundles/ticketBundleConfirmation/TicketBundleConfirmationPage')),
        urls: ['/'],
    },
    TicketBundlesPayment: {
        component: lazy(() => import('../pages/ticketBundles/ticketBundlesPayment/TicketBundlesPaymentPage')),
        urls: ['/'],
    },
    JournalLanding: {
        component: lazy(() => import('../pages/journal/landingPage/LandingPage')),
        urls: ['/'],
    },
    JournalArticle: {
        component: lazy(() => import('../pages/journal/articlePage/ArticlePage')),
        urls: ['/'],
    },
    JournalAuthor: {
        component: lazy(() => import('../pages/journal/authorPage/AuthorPage')),
        urls: ['/'],
    },
    Films: {
        component: lazy(() => import('../pages/generic/GenericPage')),
        urls: ['/'],
    },
    FilmDetails: {
        parent: 'Films',
        component: lazy(() => import('../pages/filmDetails/FilmDetailsPage')),
        urls: ['/'],
        params: ['filmName', 'filmId'],
        customAlias: 'filmDetails',
    },
    AllEvents: {
        component: lazy(() => import('@/pages/events/allEvents/AllEventsPage')),
        urls: ['/'],
    },
    EventDetails: {
        parent: 'AllEvents',
        component: lazy(() => import('@/pages/events/event/EventPage')),
        urls: ['/'],
        params: ['eventName', 'eventId'],
        customAlias: 'eventDetails',
    },
    ErrorNotFound: {
        component: lazy(() => import('../pages/404/NotFoundPage')),
        urls: ['/'],
    },
    ReviewTicket: {
        component: lazy(() => import('../pages/ticketing/review-ticket/ReviewTicketPage')),
        urls: ['/'],
    },
    MembershipSales: {
        component: lazy(() => import('./membership/sales/SalesPage')),
        urls: ['/'],
    },
    Membership: {
        component: lazy(() => import('./membership/flow/MembershipFlowPage')),
        urls: ['/'],
        subPages: [
            {
                alias: 'MembershipVoucher',
                component: lazy(() => import('../pages/membership/flow/subpages/voucher/VoucherPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'MembershipPackageSelection',
                component: lazy(
                    () => import('../pages/membership/flow/subpages/packageSelection/PackageSelectionPage')
                ),
                urls: [],
                params: [],
            },
            {
                alias: 'MembershipSignUp',
                component: lazy(() => import('../pages/membership/flow/subpages/signUp/SignUpPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'MembershipPhotoUpload',
                component: lazy(() => import('../pages/membership/flow/subpages/photoUpload/PhotoUploadPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'MembershipPayment',
                component: lazy(() => import('../pages/membership/flow/subpages/payment/PaymentPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'MembershipSignUpConfirmation',
                component: lazy(
                    () => import('../pages/membership/flow/subpages/signUpConfirmation/SignUpConfirmationPage')
                ),
                urls: [],
                params: [],
            },
        ],
    },
    TicketBundles: {
        component: lazy(() => import('../pages/ticketBundles/ticketBundlesLanding/TicketBundlesLandingPage')),
        urls: ['/'],
    },
    Account: {
        component: lazy(() => import('./account/AccountLayout')),
        urls: ['/'],
        subPages: [
            {
                alias: 'AccountOverview',
                component: lazy(() => import('../pages/account/overview/OverviewPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'AccountTransactions',
                component: lazy(() => import('../pages/account/transactions/TransactionsPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'AccountWatchlist',
                component: lazy(() => import('../pages/account/watchlist/WatchlistPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'AccountBenefits',
                component: lazy(() => import('../pages/account/benefits/BenefitsPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'AccountSubscription',
                component: lazy(() => import('../pages/account/subscription/SubscriptionPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'AccountDetails',
                component: lazy(() => import('../pages/account/details/DetailsPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'AccountWallet',
                component: lazy(() => import('../pages/account/wallet/WalletPage')),
                urls: [],
                params: [],
            },
            {
                alias: 'AccountPreferences',
                component: lazy(() => import('../pages/account/preferences/PreferencesPage')),
                urls: [],
                params: [],
            },
        ],
    },
    Ticketing: {
        component: lazy(() => import('./ticketing/flow/TicketingFlowPage')),
        urls: ['/'],
        subPages: [
            {
                alias: 'SeatPicker',
                component: lazy(() => import('../pages/ticketing/flow/subpages/seat-picker/SeatPickerPage')),
                urls: [],
                params: ['showtimeId'],
            },
            {
                alias: 'TicketPicker',
                component: lazy(() => import('../pages/ticketing/flow/subpages/ticket-picker/TicketPickerPage')),
                urls: [],
                params: ['showtimeId'],
            },
            {
                alias: 'TicketingLogin',
                component: lazy(() => import('../pages/ticketing/flow/subpages/login/LoginPage')),
                urls: [],
                params: ['showtimeId'],
            },
            {
                alias: 'Concessions',
                component: lazy(() => import('../pages/ticketing/flow/subpages/concessions/ConcessionsPage')),
                params: ['showtimeId'],
                urls: [],
            },
            {
                alias: 'OrderSummary',
                component: lazy(() => import('../pages/ticketing/flow/subpages/orderSummary/OrderSummaryPage')),
                urls: [],
                params: ['showtimeId'],
            },
            {
                alias: 'OrderPayment',
                component: lazy(() => import('../pages/ticketing/flow/subpages/order-payment/OrderPaymentPage')),
                urls: [],
                params: ['showtimeId'],
            },
        ],
    },
    ResetPassword: {
        component: lazy(() => import('@/pages/auth/resetPassword/ResetPasswordPage')),
        urls: ['/'],
        params: ['resetCode'],
    },
}
